import { Route } from 'react-router-dom'
import BillingGuard from '@/features/authentication/BillingGuard'
import FeatureFlagGuard from '@/features/authentication/FeatureFlagGuard'
import FlavourGuard from '@/features/authentication/FlavourGuard'
import GroupAdminGuard from '@/features/authentication/GroupAdminGuard'
import LocationScopeGuard from '@/features/authentication/LocationScopeGuard'
import RoleGuard from '@/features/authentication/RoleGuard'
import AdminLayout, {
    SiteAdminLayout
} from '@/layout/dashboard/admin-layout/AdminLayout'

import { isBetaMode as isBeta } from '@/lib/betaMode'

// import EventCapabilityGuard from '@/features/dashboard/permissions/EventCapabilityGuard'
// import GroupCapabilityGuard from '@/features/dashboard/permissions/GroupCapabilityGuard'

const guardMap = {
    RoleGuard
    // GroupCapabilityGuard,
    // EventCapabilityGuard
}

// A section's `layout` names a shell rendered around every route in it, via a
// pathless layout route (the page renders into its <Outlet />). Looked up by
// name, like `guard`, so routeConfig.jsx needn't import the layout — the
// layout reads navConfig, which reads routeConfig, and a direct import would
// be a cycle.
const layoutMap = {
    AdminLayout,
    SiteAdminLayout
}

const isDev = import.meta.env.DEV

function resolveTitle(title, t) {
    return typeof title === 'function' ? title(t) : title
}

function wrapWithGuard(element, item) {
    let wrapped = element
    if (item.guard) {
        const Guard = guardMap[item.guard]
        wrapped = <Guard {...(item.guardProps || {})}>{wrapped}</Guard>
    }
    if (item.requiresBilling) {
        wrapped = <BillingGuard>{wrapped}</BillingGuard>
    }
    if (item.requiresFeatureFlag) {
        wrapped = (
            <FeatureFlagGuard flag={item.requiresFeatureFlag}>
                {wrapped}
            </FeatureFlagGuard>
        )
    }
    if (item.requiresFlavour) {
        wrapped = (
            <FlavourGuard allowedFlavours={item.requiresFlavour}>
                {wrapped}
            </FlavourGuard>
        )
    }
    if (item.requiresGroupAdmin) {
        wrapped = <GroupAdminGuard>{wrapped}</GroupAdminGuard>
    }
    // Outermost: a location member reaches only routes marked locationScopeAware,
    // so a typed URL fails the same way the backend gate does.
    wrapped = (
        <LocationScopeGuard
            aware={item.locationScopeAware === true}
            redirectTo={item.locationScopeRedirect}
        >
            {wrapped}
        </LocationScopeGuard>
    )
    return wrapped
}

function itemToRoute(item, keyPrefix) {
    const Component = item.component
    if (!Component) return null
    const element = wrapWithGuard(<Component />, item)
    if (item.index) {
        return <Route key={`${keyPrefix}-index`} index element={element} />
    }
    const path = item.path?.startsWith('/') ? item.path.slice(1) : item.path
    return <Route key={`${keyPrefix}-${path}`} path={path} element={element} />
}

function getSectionRoutes(section) {
    return [...(section.items || []), ...(section.routes || [])]
}

/**
 * Returns sections array for sidebar/topnav/mobilenav.
 * Filters out dev-only sections in production.
 * Resolves translatable titles via the provided `t` function.
 */
export function getSections(config, t) {
    return config
        .filter((section) => !section.dev || isDev)
        .map((section) => ({
            subheader: resolveTitle(section.subheader, t),
            roles: section.roles,
            visible: true,
            items: getSectionRoutes(section).map((item) => ({
                title: resolveTitle(item.title, t),
                path: item.path,
                icon: item.icon,
                visible: true,
                ...(item.items
                    ? {
                          items: item.items.map((sub) => ({
                              title: resolveTitle(sub.title, t),
                              path: sub.path,
                              visible: true
                          }))
                      }
                    : {})
            }))
        }))
}

/**
 * Builds <Route> elements for all dashboard routes (items + routes arrays).
 * Used inside NavRoutes.jsx.
 */
export function buildDashboardRoutes(config) {
    const routes = []
    for (const section of config) {
        if (section.dev && !isDev) continue
        const keyPrefix = `route-${section.id ?? section.subheader ?? 'section'}`
        const sectionRoutes = getSectionRoutes(section)
            .map((item) => itemToRoute(item, keyPrefix))
            .filter(Boolean)
        const Layout = section.layout ? layoutMap[section.layout] : null
        if (Layout) {
            routes.push(
                <Route key={`${keyPrefix}-layout`} element={<Layout />}>
                    {sectionRoutes}
                </Route>
            )
        } else {
            routes.push(...sectionRoutes)
        }
    }
    return routes
}

/**
 * Builds <Route> elements for public routes only (items and routes with public: true).
 * Used in App.jsx for unauthenticated MainLayout.
 */
export function buildPublicRoutes(config) {
    if (isBeta) return []
    const routes = []
    for (const section of config) {
        for (const item of getSectionRoutes(section)) {
            if (!item.public || !item.component) continue
            const Component = item.component
            const path = item.path?.startsWith('/')
                ? item.path.slice(1)
                : item.path
            routes.push(
                <Route
                    key={`public-${path}`}
                    path={path}
                    element={<Component />}
                />
            )
        }
    }
    return routes
}
